import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import DataTable from 'react-data-table-component';
import { MdBusiness, MdAdd, MdEdit, MdOpenInNew, MdRefresh } from 'react-icons/md';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import StatsGrid from '../../../shared/layout/containers/StatsGrid';
import StatCard from '../../../shared/layout/cards/StatCard';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../shared/layout/feedback/ErrorMessage';
import { SimpleSelect } from '../../../shared/layout/forms/AppSelect';
import CountrySelect from '../../../shared/layout/forms/CountrySelect';
import { createAdminOrganization, fetchAdminOrganizations, updateAdminOrganization } from '../../../services/Http/AdminApi';
import { fetchCountryCodes, fetchOrganizationTypes } from '../../../services/Http/Api';

const AdminOrganizations = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const theme = useTheme();
    const [organizations, setOrganizations] = useState([]);
    const [types, setTypes] = useState([]);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [totalRows, setTotalRows] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(defaultForm());

    useEffect(() => {
        loadTypes();
        loadCountries();
    }, []);

    useEffect(() => {
        loadOrganizations();
    }, [currentPage, perPage, search, typeFilter]);

    const loadTypes = async () => {
        try {
            const data = await fetchOrganizationTypes();
            setTypes(Array.isArray(data) ? data : []);
        } catch (err) {
            // non-blocking
        }
    };

    const loadCountries = async () => {
        try {
            const data = await fetchCountryCodes();
            setCountries(Array.isArray(data) ? data : []);
        } catch (err) {
            // non-blocking
        }
    };

    const loadOrganizations = async () => {
        try {
            setLoading(true);
            const resp = await fetchAdminOrganizations({
                per_page: perPage,
                page: currentPage,
                search,
                organization_type_id: typeFilter,
            });
            const data = resp?.data || resp || [];
            setOrganizations(data);
            setTotalRows(resp?.meta?.total ?? data.length);
        } catch (err) {
            setError(null);
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingId) {
                await updateAdminOrganization(editingId, form);
            } else {
                await createAdminOrganization(form);
            }
            resetForm();
            setShowModal(false);
            await loadOrganizations();
        } catch (err) {
            setError(err);
        }
    };

    const columns = useMemo(() => [
        {
            name: t('AdminOrganizations.columns.name'),
            selector: (row) => row.name,
            sortable: true,
        },
        {
            name: t('AdminOrganizations.columns.type'),
            selector: (row) => typeLabel(row.organization_type_id, types),
        },
        {
            name: t('AdminOrganizations.columns.country'),
            selector: (row) => countryLabel(row.country_id, countries),
        },
        {
            name: t('AdminOrganizations.columns.email'),
            selector: (row) => row.email || '-',
        },
        {
            name: t('AdminOrganizations.columns.active'),
            selector: (row) => (row.active ? t('Common.yes') : t('Common.no')),
            sortable: true,
        },
        {
            name: t('AdminOrganizations.columns.actions'),
            cell: (row) => (
                <InlineActions>
                    <IconButton type="button" onClick={() => handleEdit(row)}>
                        <MdEdit size={18} />
                    </IconButton>
                    <IconButton type="button" onClick={() => navigate(`/organizations/${row.id}`)} title={t('Common.open')}>
                        <MdOpenInNew size={18} />
                    </IconButton>
                </InlineActions>
            ),
            ignoreRowClick: true,
        },
    ], [countries, navigate, t, types]);

    const handleEdit = (org) => {
        setEditingId(org.id);
        setForm({
            name: org.name || '',
            email: org.email || '',
            phone: org.phone || '',
            address: org.address || '',
            country_id: org.country_id || '',
            organization_type_id: org.organization_type_id || '',
            active: org.active ?? true,
        });
        setShowModal(true);
    };

    const resetForm = () => {
        setEditingId(null);
        setForm(defaultForm());
    };

    const totalOrgs = totalRows || organizations.length;
    const tableStyles = useMemo(() => getAdminTableStyles(theme), [theme]);

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle>{t('AdminOrganizations.title')}</PageTitle>
            </PageHeader>
            <PageContent>
                <StatsGrid>
                    <StatCard icon={<MdBusiness size={40} />} title={t('AdminOrganizations.stats.total')} value={totalOrgs.toLocaleString()} color="venues" />
                </StatsGrid>

                <FiltersBar>
                    <SearchInput
                        placeholder={t('AdminOrganizations.filters.searchPlaceholder')}
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                    />
                    <Select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }}>
                        <option value="">{t('AdminOrganizations.filters.allTypes')}</option>
                        {types.map((t) => (
                            <option key={t.id || t.value} value={t.id || t.value}>{t.name || t.label}</option>
                        ))}
                    </Select>
                    <IconButton type="button" onClick={() => loadOrganizations()} title={t('Common.refresh')}>
                        <MdRefresh size={18} />
                    </IconButton>
                    <PrimaryButton type="button" onClick={() => { resetForm(); setShowModal(true); }}>
                        <MdAdd size={18} />
                        {t('AdminOrganizations.actions.add')}
                    </PrimaryButton>
                </FiltersBar>

                {error && <ErrorMessage>{t('AdminOrganizations.errors.load')}</ErrorMessage>}
                <DataTable
                    columns={columns}
                    data={organizations}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    onChangePage={(page) => setCurrentPage(page)}
                    onChangeRowsPerPage={(newPerPage, page) => {
                        setPerPage(newPerPage);
                        setCurrentPage(page);
                    }}
                    highlightOnHover
                    progressPending={loading}
                    progressComponent={<LoadingMessage>{t('AdminOrganizations.loading')}</LoadingMessage>}
                    paginationPerPage={perPage}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    persistTableHead
                    onRowClicked={(row) => navigate(`/organizations/${row.id}`)}
                    pointerOnHover
                    customStyles={tableStyles}
                    noDataComponent={t('AdminOrganizations.empty')}
                />
            </PageContent>

            {showModal && (
                <ModalOverlay>
                    <ModalCard>
                        <ModalHeader>
                            <h4>{editingId ? t('AdminOrganizations.modal.editTitle') : t('AdminOrganizations.modal.addTitle')}</h4>
                            <IconButton type="button" onClick={() => { setShowModal(false); resetForm(); }}>
                                ✕
                            </IconButton>
                        </ModalHeader>
                        <Form onSubmit={handleSubmit}>
                            <Row>
                                <Input name="name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder={t('Common.name')} required />
                                <Select name="organization_type_id" value={form.organization_type_id} onChange={(e) => setForm((f) => ({ ...f, organization_type_id: e.target.value }))} required>
                                    <option value="">{t('AdminOrganizations.form.organizationType')}</option>
                                    {types.map((t) => (
                                        <option key={t.id || t.value} value={t.id || t.value}>{t.name || t.label}</option>
                                    ))}
                                </Select>
                            </Row>
                            <Row>
                                <Input name="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder={t('Common.email')} type="email" />
                                <Input name="phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder={t('Common.phone')} />
                            </Row>
                            <Row>
                                <CountrySelect
                                    countries={countries}
                                    value={form.country_id}
                                    onChange={(_, nextValue) => setForm((f) => ({ ...f, country_id: nextValue || '' }))}
                                    inputId="organization-country"
                                    placeholder={t('Common.country')}
                                    menuPortalTarget={document.body}
                                />
                                <Input name="address" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} placeholder={t('Common.address')} />
                            </Row>
                            <Row>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={!!form.active}
                                        onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                                    />
                                    <span style={{ marginLeft: 8 }}>{t('Common.active')}</span>
                                </label>
                            </Row>
                            <Actions>
                                <PrimaryButton type="submit">{editingId ? t('Common.saveChanges') : t('AdminOrganizations.actions.create')}</PrimaryButton>
                                <SecondaryButton type="button" onClick={() => { setShowModal(false); resetForm(); }}>{t('Common.cancel')}</SecondaryButton>
                            </Actions>
                        </Form>
                    </ModalCard>
                </ModalOverlay>
            )}
        </PageContainer>
    );
};

const typeLabel = (id, types) => {
    const match = types.find((t) => String(t.id || t.value) === String(id));
    return match ? (match.name || match.label) : '-';
};

const countryLabel = (id, countries) => {
    const match = countries.find((c) => String(c.id || c.code) === String(id));
    return match ? match.name : '-';
};

const defaultForm = () => ({
    name: '',
    email: '',
    phone: '',
    address: '',
    country_id: '',
    organization_type_id: '',
    active: true,
});

const FiltersBar = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 0.625rem;
    margin: 0.75rem 0;
`;

const SearchInput = styled.input`
    padding: 0.75rem 0.875rem;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 10px;
    width: 100%;
    font-size: 0.95rem;
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.textPrimary};
    transition: border 0.2s ease, box-shadow 0.2s ease;

    &:focus {
        border-color: var(--color-primary);
        box-shadow: 0 0 0 3px var(--color-primary-bg);
        outline: none;
    }
`;

const Select = styled(SimpleSelect)`
    padding: 0.75rem 0.875rem;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 10px;
    width: 100%;
    font-size: 0.95rem;
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.textPrimary};
    transition: border 0.2s ease, box-shadow 0.2s ease;

    &:focus {
        border-color: var(--color-primary);
        box-shadow: 0 0 0 3px var(--color-primary-bg);
        outline: none;
    }
`;

const PrimaryButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: linear-gradient(135deg, var(--color-primary), var(--color-primary-light));
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 0.75rem 1rem;
    cursor: pointer;
    font-weight: 700;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.9;
    }
`;

const SecondaryButton = styled.button`
    background: ${({ theme }) => theme.colors.surfaceAlt};
    color: ${({ theme }) => theme.colors.textPrimary};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 10px;
    padding: 0.75rem 1rem;
    cursor: pointer;
    font-weight: 600;
    transition: background 0.2s ease;

    &:hover {
        background: ${({ theme }) => theme.colors.surfaceMuted};
    }
`;

const InlineActions = styled.div`
    display: flex;
    gap: 0.5rem;
`;

const IconButton = styled.button`
    background: ${({ theme }) => theme.colors.surfaceAlt};
    border: 1px solid ${({ theme }) => theme.colors.border};
    color: ${({ theme }) => theme.colors.textPrimary};
    border-radius: 8px;
    padding: 0.375rem 0.5rem;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s ease, border 0.2s ease;

    &:hover {
        background: var(--color-primary-bg-soft);
        border-color: var(--color-primary);
        color: var(--color-primary);
    }
`;

const ModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    z-index: 2000;
`;

const ModalCard = styled.div`
    background: ${({ theme }) => theme.colors.surface};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 12px;
    padding: 1rem;
    width: min(700px, 100%);
    box-shadow: 0 10px 30px rgba(0,0,0,0.12);
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.625rem;

    h4 {
        margin: 0;
        color: ${({ theme }) => theme.colors.textPrimary};
    }
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

const Row = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 0.75rem;
`;

const Input = styled.input`
    padding: 0.75rem 0.875rem;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 10px;
    width: 100%;
    font-size: 0.95rem;
    transition: border 0.2s ease, box-shadow 0.2s ease;

    &:focus {
        border-color: var(--color-primary);
        box-shadow: 0 0 0 3px var(--color-primary-bg);
        outline: none;
    }
`;

const Actions = styled.div`
    display: flex;
    gap: 0.625rem;
`;

const getAdminTableStyles = (theme) => ({
    headRow: {
        style: {
            backgroundColor: theme.colors.tableHeaderBg || '#f9fafb',
            border: `1px solid ${theme.colors.border}`,
            borderBottom: `1px solid ${theme.colors.border}`,
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            minHeight: '48px',
        },
    },
    headCells: {
        style: {
            color: theme.colors.textMuted,
            fontSize: '12px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
        },
    },
    rows: {
        style: {
            backgroundColor: theme.colors.surface,
            color: theme.colors.textPrimary,
            borderLeft: `1px solid ${theme.colors.border}`,
            borderRight: `1px solid ${theme.colors.border}`,
            borderBottom: `1px solid ${theme.colors.border}`,
        },
        highlightOnHoverStyle: {
            backgroundColor: theme.colors.tableRowHoverBg || '#f8fafc',
            color: theme.colors.textPrimary,
        },
    },
    pagination: {
        style: {
            backgroundColor: theme.colors.surface,
            color: theme.colors.textPrimary,
            border: `1px solid ${theme.colors.border}`,
            borderTop: 'none',
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
        },
    },
});

export default AdminOrganizations;
